export const RACE_TAX_CHECK_IDS = [
  "policy",
  "location",
  "coverage",
  "stripe_settings",
  "registrations",
] as const;

export type RaceTaxCheckId = (typeof RACE_TAX_CHECK_IDS)[number];
export type RaceTaxCheckStatus =
  | "complete"
  | "action_required"
  | "blocked"
  | "not_required"
  | "unavailable";

export type RaceTaxReadiness = {
  raceId: string;
  checkedAt: string;
  paid: boolean;
  ready: boolean;
  jurisdiction: string | null;
  policyVersion: string | null;
  liableParty: "organizer" | "rauxa" | null;
  checks: Array<{
    id: RaceTaxCheckId;
    status: RaceTaxCheckStatus;
    reason: string | null;
  }>;
  requirements: Array<{
    type: string | null;
    jurisdiction: string | null;
    status: "complete" | "missing";
  }>;
};

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
const optionalString = (value: unknown): string | null =>
  typeof value === "string" && value.trim() ? value.trim() : null;
const acceptedStatuses: readonly string[] = [
  "complete", "action_required", "blocked", "not_required", "unavailable",
];

export const parseRaceTaxReadiness = (
  value: unknown,
  expectedRaceId: string
): RaceTaxReadiness => {
  const record = asRecord(value);
  const raceId = optionalString(record.raceId);
  const checkedAt = optionalString(record.checkedAt);
  if (raceId !== expectedRaceId || !checkedAt || !Number.isFinite(Date.parse(checkedAt))) {
    throw new Error("Unable to load this race's tax readiness. Try refreshing.");
  }

  const rawChecks = Array.isArray(record.checks) ? record.checks.map(asRecord) : [];
  const checks: RaceTaxReadiness["checks"] = RACE_TAX_CHECK_IDS.map((id) => {
    const matches = rawChecks.filter((entry) => entry.id === id);
    const check = matches.length === 1 ? matches[0] : {};
    return {
      id,
      status: acceptedStatuses.includes(String(check.status))
        ? (check.status as RaceTaxCheckStatus)
        : "unavailable",
      reason: optionalString(check.reason),
    };
  });
  const requirements: RaceTaxReadiness["requirements"] = Array.isArray(record.requirements)
    ? record.requirements.map((entry) => {
        const requirement = asRecord(entry);
        return {
          type: optionalString(requirement.type),
          jurisdiction: optionalString(requirement.jurisdiction),
          status: requirement.status === "complete" ? "complete" : "missing",
        };
      })
    : [];
  const structureComplete =
    typeof record.paid === "boolean" &&
    rawChecks.length === RACE_TAX_CHECK_IDS.length &&
    Array.isArray(record.requirements);
  return {
    raceId,
    checkedAt,
    paid: record.paid !== false,
    ready:
      record.ready === true &&
      structureComplete &&
      checks.every(({ id, status }) =>
        status === "complete" ||
        (status === "not_required" && (record.paid === false || id === "registrations"))
      ) &&
      requirements.every(({ status }) => status === "complete"),
    jurisdiction: optionalString(record.jurisdiction),
    policyVersion: optionalString(record.policyVersion),
    liableParty: record.liableParty === "organizer" || record.liableParty === "rauxa"
      ? record.liableParty
      : null,
    checks,
    requirements,
  };
};

export const canApproveRaceWithTaxReadiness = (
  raceId: string,
  paid: boolean,
  readiness: RaceTaxReadiness | null
): boolean => !paid || Boolean(readiness?.raceId === raceId && readiness.paid && readiness.ready);
